import { audioContext } from '../audioContext';

export class ReverbNode {
   constructor({
      color = 'pink', //brown white
      decay = 1.5,
      wet = 1,
      dry = 1,
      predelay = 0,
      preWidth = 0.25, // stereo merge may be a better name
   } = {}) {
      // non-node parameter values
      this._color = color;
      this._decay = decay; //seconds
      this._preWidth = preWidth;

      // create nodes
      this.in = new GainNode(audioContext);

      this.monoGainAdjustment = new GainNode(audioContext);
      this.toMonoSplitter = new ChannelSplitterNode(audioContext);
      this.monoMerger = new ChannelMergerNode(audioContext);

      this.monoAmount = new GainNode(audioContext);
      this.stereoAmount = new GainNode(audioContext);

      this.predelay = new DelayNode(audioContext);

      this.convolver = new ConvolverNode(audioContext);

      this.wet = new GainNode(audioContext);
      this.dry = new GainNode(audioContext);

      this.out = new GainNode(audioContext);

      // set defaults
      const now = audioContext.currentTime;

      // signals are doubled when merging to mono, so halve them
      this.monoGainAdjustment.gain.setValueAtTime(0.5, now);

      this.wet.gain.setValueAtTime(wet, now);
      this.dry.gain.setValueAtTime(dry, now);
      this.predelay.delayTime.setValueAtTime(predelay, now);

      this.stereoAmount.gain.setValueAtTime(preWidth, now);
      this.monoAmount.gain.setValueAtTime(1 - preWidth, now);

      // connect nodes

      this.in.connect(this.monoGainAdjustment);
      this.monoGainAdjustment.connect(this.toMonoSplitter);
      this.toMonoSplitter.connect(this.monoMerger, 0, 0);
      this.toMonoSplitter.connect(this.monoMerger, 0, 1);
      this.toMonoSplitter.connect(this.monoMerger, 1, 0);
      this.toMonoSplitter.connect(this.monoMerger, 1, 1);

      this.monoMerger.connect(this.monoAmount);
      this.in.connect(this.stereoAmount);

      this.monoAmount.connect(this.predelay);
      this.stereoAmount.connect(this.predelay);
      this.predelay.connect(this.convolver);

      this.in.connect(this.dry);
      this.convolver.connect(this.wet);

      this.wet.connect(this.out);
      this.dry.connect(this.out);

      // create initial buffer
      this.updateConvolverBuffer();
   }

   set color(value) {
      this._color = value;
      this.updateConvolverBuffer();
   }

   get color() {
      return this._color;
   }

   set decay(value) {
      this._decay = value;
      this.updateConvolverBuffer();
   }
   get decay() {
      return this._decay;
   }

   set preWidth(value) {
      const now = audioContext.currentTime;

      this.stereoAmount.gain.linearRampToValueAtTime(value, now + 0.01);
      this.monoAmount.gain.linearRampToValueAtTime(1 - value, now + 0.01);

      this._preWidth = value;
   }
   get preWidth() {
      return this._preWidth;
   }

   updateConvolverBuffer() {
      const { color, decay } = this;

      const offlineContext = new OfflineAudioContext(
         2,
         decay * audioContext.sampleRate,
         audioContext.sampleRate
      );

      const noiseBufferSource = new AudioBufferSourceNode(offlineContext, {
         buffer: noiseBuffer[color],
         loop: true,
      });

      const gain = new GainNode(offlineContext);
      gain.connect(offlineContext.destination);

      noiseBufferSource.connect(gain);
      gain.gain.setValueAtTime(1, 0);

      const decayNearlyComplete = decay * 0.95;
      gain.gain.exponentialRampToValueAtTime(
         0.001, // close enough to silence for this purpose
         decayNearlyComplete
      );
      gain.gain.cancelAndHoldAtTime(decayNearlyComplete);
      gain.gain.linearRampToValueAtTime(0, decay);

      noiseBufferSource.start(0);

      offlineContext.oncomplete = ({ renderedBuffer }) => {
         // in case of subsequent changes to these that resolve out of order, only resolve the one that matches the current state
         if (color === this.color && decay === this.decay) {
            this.convolver.buffer = renderedBuffer;
         }
      };

      offlineContext.startRendering();
   }

   connect(...args) {
      this.gainNode.connect(...args);
   }
}

// cache for white/pink/brown noise buffers - could be used for more than just this reverb in the future
class NoiseBuffer {
   constructor() {
      this.noiseBuffers = {};
   }

   get white() {
      if (this.noiseBuffers.white == null) {
         const BUFFER_LENGTH = audioContext.sampleRate * 5;

         this.noiseBuffers.white = new AudioBuffer({
            numberOfChannels: 2,
            length: BUFFER_LENGTH,
            sampleRate: audioContext.sampleRate,
         });
         for (let channelIndex = 0; channelIndex < 2; channelIndex++) {
            const channel = new Float32Array(BUFFER_LENGTH);

            for (let i = 0; i < BUFFER_LENGTH; i++) {
               channel[i] = Math.random() * 2 - 1;
            }

            this.noiseBuffers.white.copyToChannel(channel, channelIndex);
         }
      }

      return this.noiseBuffers.white;
   }

   get pink() {
      if (this.noiseBuffers.pink == null) {
         const BUFFER_LENGTH = audioContext.sampleRate * 5;

         this.noiseBuffers.pink = new AudioBuffer({
            numberOfChannels: 2,
            length: BUFFER_LENGTH,
            sampleRate: audioContext.sampleRate,
         });
         for (let channelIndex = 0; channelIndex < 2; channelIndex++) {
            const channel = new Float32Array(BUFFER_LENGTH);

            let b0, b1, b2, b3, b4, b5, b6;
            b0 = b1 = b2 = b3 = b4 = b5 = b6 = 0.0;
            for (let i = 0; i < BUFFER_LENGTH; i++) {
               const white = Math.random() * 2 - 1;
               b0 = 0.99886 * b0 + white * 0.0555179;
               b1 = 0.99332 * b1 + white * 0.0750759;
               b2 = 0.969 * b2 + white * 0.153852;
               b3 = 0.8665 * b3 + white * 0.3104856;
               b4 = 0.55 * b4 + white * 0.5329522;
               b5 = -0.7616 * b5 - white * 0.016898;
               channel[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + white * 0.5362;
               channel[i] *= 0.11; // (roughly) compensate for gain
               b6 = white * 0.115926;
            }

            this.noiseBuffers.pink.copyToChannel(channel, channelIndex);
         }
      }

      return this.noiseBuffers.pink;
   }

   get brown() {
      if (this.noiseBuffers.brown == null) {
         const BUFFER_LENGTH = audioContext.sampleRate * 5;

         this.noiseBuffers.brown = new AudioBuffer({
            numberOfChannels: 2,
            length: BUFFER_LENGTH,
            sampleRate: audioContext.sampleRate,
         });
         for (let channelIndex = 0; channelIndex < 2; channelIndex++) {
            const channel = new Float32Array(BUFFER_LENGTH);

            let lastOut = 0.0;
            for (let i = 0; i < BUFFER_LENGTH; i++) {
               const white = Math.random() * 2 - 1;
               channel[i] = (lastOut + 0.02 * white) / 1.02;
               lastOut = channel[i];
               channel[i] *= 3.5; // (roughly) compensate for gain
            }

            this.noiseBuffers.brown.copyToChannel(channel, channelIndex);
         }
      }
      return this.noiseBuffers.brown;
   }
}

const noiseBuffer = new NoiseBuffer();
